/**
 * Admin Portal → Codes → All codes → Code batches — `/admin/codes/all/batches` and
 * `/admin/codes/all/batches/:batchId` (Codes PR-8 moved it; the old addresses redirect) (Codes PR-5, migration 1877; docs/architecture/38-PARTNER-
 * ATTRIBUTION.md §16).
 *
 * Every code Coach Cedar issues belongs to ONE batch: an admin form's run, a partner API key's
 * day, the referral system's month. The list shows who, how, why and when, and counts DERIVED from
 * the batch's codes when read (never stored); a row opens the batch's codes. Whole codes and the
 * CSV export are for admin / super_admin; support and viewer see codes masked. The counter check
 * compares the stored counters with their rows; codes with no batch are information, and a
 * super_admin can file them with "Adopt unbatched codes" (the server runs migration 1877's own
 * backfill SQL — never a re-run of the migration).
 *
 * Web-only (the admin SPA): react-router is fine here.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AdminButton, AdminCard, SectionHeader } from '../../../components/admin/ui';
import {
  NoticeLine,
  ReadOnlyNote,
  errText,
  fmtDateTime,
  hint,
  input,
  label,
  row,
  saveBlob,
  td,
  th,
  useAdminRole,
  useBusy,
  type Notice,
} from '../partners/PartnersShared';
import { CodesAdmin } from './codesAdminService';
import {
  BATCH_CODES_PAGE_SIZE,
  BATCH_CREATED_VIA,
  BATCH_OWNERS,
  BATCH_PAGE_SIZE,
  CREATED_VIA_LABELS,
  adoptSummary,
  batchOwnerLabel,
  canAdopt,
  counterCheckFindings,
  shortId,
  statesSummary,
  type BatchCode,
  type BatchCreatedVia,
  type BatchOwner,
  type BatchRow,
  type CounterCheck,
  unbatchedTotal,
} from './codesAdminLogic';
import { CODES_ROUTES, FAMILY_VOCAB_NOTE, batchPath } from './codesAreaLogic';
import { VocabNote } from './CodesArea';

const TONE_MARK = { warn: '⚠', info: 'ℹ', ok: '✓' } as const;

const OWNER_LABELS: Record<BatchOwner, string> = { partner: 'Partners', house: 'Coach Cedar', campaign: 'Summer50 & referral' };

/** `embedded`: inside the Codes area's All codes screen (its header and tabs above; no page padding of its own). */
export function AdminCodeBatches({ embedded = false }: { embedded?: boolean } = {}) {
  const { batchId } = useParams();
  return batchId ? <BatchDetail batchId={batchId} embedded={embedded} /> : <BatchList embedded={embedded} />;
}

// ── the list ─────────────────────────────────────────────────────────────────

const BatchList: React.FC<{ embedded: boolean }> = ({ embedded }) => {
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const owner = (search.get('owner') || '') as BatchOwner | '';
  const createdVia = (search.get('created_via') || '') as BatchCreatedVia | '';
  const offset = Math.max(0, Number(search.get('offset') || 0) || 0);
  const [rows, setRows] = useState<BatchRow[]>([]);
  const [total, setTotal] = useState(0);
  const [notice, setNotice] = useState<Notice>(null);
  const [check, setCheck] = useState<CounterCheck | null>(null);
  const [busy, run] = useBusy();
  const { role } = useAdminRole();

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(search);
    if (value) next.set(key, value); else next.delete(key);
    next.delete('offset');
    setSearch(next, { replace: true });
  };

  useEffect(() => {
    let live = true;
    CodesAdmin.listBatches({ owner, createdVia, offset, limit: BATCH_PAGE_SIZE })
      .then((r) => { if (live) { setRows(r.batches || []); setTotal(r.total || 0); } })
      .catch((e) => { if (live) setNotice({ kind: 'error', text: errText(e) }); });
    return () => { live = false; };
  }, [owner, createdVia, offset]);

  const runCheck = () =>
    run(async () => {
      try {
        setCheck(await CodesAdmin.counterCheck());
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const adopt = () =>
    run(async () => {
      try {
        const res = await CodesAdmin.adoptUnbatched();
        setNotice({ kind: 'ok', text: adoptSummary(res) });
        setCheck(await CodesAdmin.counterCheck());
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  const page = (to: number) => {
    const next = new URLSearchParams(search);
    if (to > 0) next.set('offset', String(to)); else next.delete('offset');
    setSearch(next, { replace: true });
  };

  return (
    <div style={embedded ? undefined : { padding: 24, maxWidth: 1280 }}>
      <SectionHeader title="Code batches" subtitle="Every code belongs to one batch — who issued it, how, why and when. Counts are read from the codes themselves." />
      <VocabNote text={FAMILY_VOCAB_NOTE} />
      <NoticeLine notice={notice} onClose={() => setNotice(null)} />
      <div style={{ ...row, marginBottom: 12 }}>
        <div>
          <label style={label} htmlFor="cb-owner">Whose codes</label>
          <select id="cb-owner" style={input} value={owner} onChange={(e) => setFilter('owner', e.target.value)}>
            <option value="">Everyone</option>
            {BATCH_OWNERS.map((o) => <option key={o} value={o}>{OWNER_LABELS[o]}</option>)}
          </select>
        </div>
        <div>
          <label style={label} htmlFor="cb-via">Created via</label>
          <select id="cb-via" style={input} value={createdVia} onChange={(e) => setFilter('created_via', e.target.value)}>
            <option value="">Any</option>
            {BATCH_CREATED_VIA.map((v) => <option key={v} value={v}>{CREATED_VIA_LABELS[v]}</option>)}
          </select>
        </div>
        <Link to={CODES_ROUTES.issueHouse} style={{ fontSize: 13 }}>Coach Cedar codes →</Link>
        <AdminButton size="sm" variant="secondary" onClick={runCheck} disabled={busy}>Check the counters</AdminButton>
      </div>
      {check && (
        <AdminCard variant="subtle">
          <div data-testid="counter-check">
            {counterCheckFindings(check).map((f) => <div key={f.text} style={{ fontSize: 13 }}>{TONE_MARK[f.tone]} {f.text}</div>)}
          </div>
          {canAdopt(role) && unbatchedTotal(check) > 0 && (
            <div style={{ marginTop: 8 }}>
              <AdminButton size="sm" variant="secondary" onClick={adopt} disabled={busy}>Adopt unbatched codes</AdminButton>
            </div>
          )}
        </AdminCard>
      )}
      <AdminCard flush>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Created</th><th style={th}>Whose</th><th style={th}>Kind</th><th style={th}>Via</th>
              <th style={th}>By</th><th style={th}>Purpose</th><th style={th}>Asked</th><th style={th}>Codes</th>
              <th style={th}>Redemptions</th><th style={th}>States</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td style={td} colSpan={10}>No batches.</td></tr>}
            {rows.map((b) => (
              <tr key={b.id} data-testid={`batch-${b.id}`} style={{ cursor: 'pointer' }}
                onClick={() => navigate(batchPath(b.id))}>
                <td style={td}>{fmtDateTime(b.created_at)}</td>
                <td style={td}>{batchOwnerLabel(b)}</td>
                <td style={td}>{b.kind ? b.kind.replace(/_/g, ' ') : '—'}</td>
                <td style={td}>{CREATED_VIA_LABELS[b.created_via] ?? b.created_via}{b.api_day ? ` · ${b.api_day}` : ''}</td>
                <td style={td}>{b.created_by ?? '—'}</td>
                <td style={td}>{b.purpose ?? '—'}</td>
                <td style={td}>{b.requested_count ?? '—'}</td>
                <td style={td}>{b.codes}</td>
                <td style={td}>{b.redemptions}</td>
                <td style={td}>{statesSummary(b.states)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </AdminCard>
      <div style={{ ...row, marginTop: 8 }}>
        <span style={hint}>{total} batch{total === 1 ? '' : 'es'}</span>
        {offset > 0 && <AdminButton size="sm" variant="ghost" onClick={() => page(Math.max(0, offset - BATCH_PAGE_SIZE))}>← Newer</AdminButton>}
        {offset + BATCH_PAGE_SIZE < total && <AdminButton size="sm" variant="ghost" onClick={() => page(offset + BATCH_PAGE_SIZE)}>Older →</AdminButton>}
      </div>
    </div>
  );
};

// ── one batch ────────────────────────────────────────────────────────────────

const BatchDetail: React.FC<{ batchId: string; embedded: boolean }> = ({ batchId, embedded }) => {
  const { writable, loaded } = useAdminRole();
  const [batch, setBatch] = useState<BatchRow | null>(null);
  const [codes, setCodes] = useState<BatchCode[]>([]);
  const [total, setTotal] = useState(0);
  const [masked, setMasked] = useState(true);
  const [offset, setOffset] = useState(0);
  const [notice, setNotice] = useState<Notice>(null);
  const [busy, run] = useBusy();

  const load = useCallback(async () => {
    try {
      const [b, c] = await Promise.all([
        CodesAdmin.getBatch(batchId),
        CodesAdmin.batchCodes(batchId, BATCH_CODES_PAGE_SIZE, offset),
      ]);
      setBatch(b.batch);
      setCodes(c.codes || []);
      setTotal(c.total || 0);
      setMasked(!!c.codes_masked);
    } catch (e) {
      setNotice({ kind: 'error', text: errText(e) });
    }
  }, [batchId, offset]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () =>
    run(async () => {
      try {
        const file = await CodesAdmin.exportBatch(batchId);
        if (!saveBlob(file.blob, file.filename)) setNotice({ kind: 'error', text: 'The browser refused the download.' });
      } catch (e) {
        setNotice({ kind: 'error', text: errText(e) });
      }
    });

  return (
    <div style={embedded ? undefined : { padding: 24, maxWidth: 1280 }}>
      <div style={{ fontSize: 13, marginBottom: 8 }}><Link to={CODES_ROUTES.batches}>← Code batches</Link></div>
      <SectionHeader
        title={batch ? `Batch ${shortId(batch.id)} — ${batchOwnerLabel(batch)}` : 'Batch'}
        subtitle={batch?.purpose ?? undefined}
      />
      <ReadOnlyNote writable={writable} loaded={loaded} />
      <NoticeLine notice={notice} onClose={() => setNotice(null)} />
      {batch && (
        <AdminCard>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', fontSize: 13 }} data-testid="batch-meta">
            <div><div style={hint}>Created</div>{fmtDateTime(batch.created_at)}</div>
            <div><div style={hint}>Via</div>{CREATED_VIA_LABELS[batch.created_via] ?? batch.created_via}{batch.api_day ? ` (${batch.api_day}, key ${shortId(batch.api_key_id)})` : ''}</div>
            <div><div style={hint}>By (admin id)</div>{batch.created_by ?? '—'}</div>
            <div><div style={hint}>Kind</div>{batch.kind ? batch.kind.replace(/_/g, ' ') : '—'}</div>
            <div><div style={hint}>Asked for</div>{batch.requested_count ?? '—'}</div>
            <div><div style={hint}>Codes</div><b>{batch.codes}</b></div>
            <div><div style={hint}>Redemptions</div><b>{batch.redemptions}</b></div>
            <div><div style={hint}>States</div>{statesSummary(batch.states)}</div>
            <div><div style={hint}>Expires</div>{batch.expires_at ? fmtDateTime(batch.expires_at) : '—'}</div>
          </div>
          {batch.notes && <div style={{ ...hint, marginTop: 8 }}>{batch.notes}</div>}
          {batch.owner === 'house' && batch.partner_id && (
            <div style={{ ...hint, marginTop: 8 }}><Link to={CODES_ROUTES.issueHouse}>Open Coach Cedar codes</Link></div>
          )}
          {batch.owner === 'partner' && batch.partner_id && (
            <div style={{ ...hint, marginTop: 8 }}><Link to={`/admin/partners/${batch.partner_id}`}>Open the partner</Link></div>
          )}
        </AdminCard>
      )}
      <VocabNote text={FAMILY_VOCAB_NOTE} />
      <div style={{ ...row, margin: '12px 0' }}>
        {writable && <AdminButton size="sm" variant="secondary" onClick={exportCsv} disabled={busy}>Export CSV</AdminButton>}
        <Link to={`${CODES_ROUTES.all}?batch_id=${encodeURIComponent(batchId)}`} style={{ fontSize: 13 }}>Open in All codes →</Link>
        {masked && <span style={hint} data-testid="codes-masked">Codes are masked for your admin role.</span>}
      </div>
      <AdminCard flush>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={th}>Code</th><th style={th}>Kind</th><th style={th}>State</th><th style={th}>Redemptions</th>
              <th style={th}>Expires</th><th style={th}>Created</th>
            </tr>
          </thead>
          <tbody>
            {codes.length === 0 && <tr><td style={td} colSpan={6}>No codes in this batch.</td></tr>}
            {codes.map((c) => (
              <tr key={c.id}>
                <td style={td}><code>{c.code}</code>{c.tag ? <span style={hint}> · {c.tag}</span> : null}</td>
                <td style={td}>{String(c.kind).replace(/_/g, ' ')}</td>
                <td style={td}>{c.state.replace(/_/g, ' ')}</td>
                <td style={td}>{c.redemptions}{c.max_redemptions != null ? ` / ${c.max_redemptions}` : ''}</td>
                <td style={td}>{c.expires_at ? fmtDateTime(c.expires_at) : '—'}</td>
                <td style={td}>{fmtDateTime(c.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </AdminCard>
      <div style={{ ...row, marginTop: 8 }}>
        <span style={hint}>{total} code{total === 1 ? '' : 's'}</span>
        {offset > 0 && <AdminButton size="sm" variant="ghost" onClick={() => setOffset(Math.max(0, offset - BATCH_CODES_PAGE_SIZE))}>← Previous</AdminButton>}
        {offset + BATCH_CODES_PAGE_SIZE < total && <AdminButton size="sm" variant="ghost" onClick={() => setOffset(offset + BATCH_CODES_PAGE_SIZE)}>Next →</AdminButton>}
      </div>
    </div>
  );
};

export default AdminCodeBatches;
